import { Link } from 'react-router-dom'
import { Modal, Form } from "react-bootstrap";
import { useState, useEffect, useRef } from 'react';
import { useUserAPI } from '../../contexts/UserAPIContext';
import { usePagination } from '../../contexts/PaginationContext';
import { getImageUrl } from '../../firebase/storageService';
import PaginationControls from '../../components/PaginationControls';
import PageLoader from '../../components/PageLoader';
import { authFetch } from '../../utils/authFetch';

const DEFAULT_PROFILE_IMAGE = require('../../assets/img/perfilvazio.png');

const Terapeutas = () => {
  const { userAPIData, loading } = useUserAPI();
  const abortController = useRef(null);
  const imageCache = useRef({});
  const isFirstRequest = useRef(true);

  const {
    page,
    pageSize,
    setTotalElements,
    setTotalPages,
    setPage,
  } = usePagination();

  const [terapeutas, setTerapeutas] = useState([]);
  const [loadingTerap, setLoadingTerap] = useState(true);
  const [error, setError] = useState(null);
  const [showModalExportarPag, setShowModalExportarPag] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  const fetchTerapeutas = async () => {
    setLoadingTerap(true);
    const currentPage = isFirstRequest.current ? 0 : page;
    const url = `therapist/${currentPage}/${pageSize}`;

    try {
      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error("Erro ao buscar Terapeutas");
      }

      const data = await response.json();

      // Garante que não mais que 12 itens sejam exibidos
      const terapeutasData = data.content.slice(0, 12).map(terapeuta => {
        // Verifica se já temos a imagem em cache
        const cachedImage = imageCache.current[terapeuta.id];
        return {
          ...terapeuta,
          imageUrl: cachedImage || null
        };
      });

      // Inicializa o estado de loading apenas para imagens não cacheadas
      const newLoadingStates = {};
      terapeutasData.forEach(terapeuta => {
        newLoadingStates[terapeuta.id] = !imageCache.current[terapeuta.id];
      });
      setImageLoadingStates(newLoadingStates);

      setTerapeutas(terapeutasData);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      
      // Atualiza a página após o request
      setPage(currentPage);
      isFirstRequest.current = false;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTerap(false);
    }
  };

  // Efeito para carregar as imagens
  useEffect(() => {
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    const loadImages = async () => {
      if (terapeutas.length > 0) {
        // Filtra apenas terapeutas sem imagem em cache
        const terapeutasSemCache = terapeutas.filter(
          terapeuta => !imageCache.current[terapeuta.id]
        );

        const loadPromises = terapeutasSemCache.map(async (terapeuta) => {
          try {
            if (abortController.current.signal.aborted) return;

            const url = await getImageUrl('therapist', terapeuta.id.toString());
            const imageToUse = url || DEFAULT_PROFILE_IMAGE;

            if (!abortController.current.signal.aborted) {
              imageCache.current[terapeuta.id] = imageToUse;

              setTerapeutas(prevTerapeutas =>
                prevTerapeutas.map(t =>
                  t.id === terapeuta.id ? { ...t, imageUrl: imageToUse } : t
                )
              );
              setImageLoadingStates(prev => ({
                ...prev,
                [terapeuta.id]: false
              }));
            }
          } catch (error) {
            if (error.name === 'AbortError') return;

            console.error(`Erro ao carregar imagem do terapeuta ${terapeuta.id}:`, error);
            if (!abortController.current.signal.aborted) {
              imageCache.current[terapeuta.id] = DEFAULT_PROFILE_IMAGE;

              setTerapeutas(prevTerapeutas =>
                prevTerapeutas.map(t =>
                  t.id === terapeuta.id
                    ? { ...t, imageUrl: DEFAULT_PROFILE_IMAGE }
                    : t
                )
              );
              setImageLoadingStates(prev => ({
                ...prev,
                [terapeuta.id]: false
              }));
            }
          }
        });

        await Promise.all(loadPromises);
      }
    };

    loadImages();

    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [terapeutas.length]);

  // Limpa o cache quando o componente é desmontado
  useEffect(() => {
    return () => {
      imageCache.current = {};
    };
  }, []);

  useEffect(() => {
    fetchTerapeutas();
  }, [page, pageSize]);

  if (loading) return <PageLoader />;
  if (loadingTerap) return <PageLoader />;
  if (error) return <p>Erro: {error}</p>;

  return (
    <div>
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Nosso Time</h3>
          {userAPIData.userRoleId === 1 && (
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Link to='/Terapeutas/Novo' className='btn btn-info btn-sm'>
                Cadastrar Equipe
              </Link>
              <button 
                className="btn btn-sm btn-info" 
                onClick={() => setShowModalExportarPag(true)}
              >
                Exportar Pagamentos do Mês
              </button>
              <Link to={'/Funcoes'} className="btn btn-sm btn-info">
                Gerenciar Funções
              </Link>
            </div>
          )}
        </div>

        <div className="row g-3">
          {terapeutas &&
            terapeutas.map((terapeuta) => (
              <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2" key={terapeuta.id}>
                <div className="card h-100">
                  <Link 
                    to={`/Terapeutas/PerfilTerapeuta/${terapeuta.id}`}
                    style={{ 
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center p-3">
                      <div className="position-relative mb-3" style={{
                        width: '120px',
                        height: '120px'
                      }}>
                        {imageLoadingStates[terapeuta.id] && (
                          <div className="position-absolute top-50 start-50 translate-middle">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Carregando...</span>
                            </div>
                          </div>
                        )}
                        <img
                          src={terapeuta.imageUrl || DEFAULT_PROFILE_IMAGE}
                          alt={`Foto de ${terapeuta.name}`}
                          className="img-fluid rounded-circle"
                          style={{
                            width: '120px',
                            height: '120px',
                            objectFit: 'cover',
                            opacity: terapeuta.disabled ? 0.5 : (imageLoadingStates[terapeuta.id] ? 0.3 : 1),
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                          }}
                        />
                      </div>
                      <h5 className="card-title text-center mb-0" style={{ 
                        fontSize: '1rem',
                        color: '#2c3e50',
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.3',
                        minHeight: '2.6em'
                      }}>
                        {terapeuta.name}
                      </h5>
                    </div>
                  </Link>
                </div>
              </div>
            ))}
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <PaginationControls currentCount={terapeutas.length} />
          </div>
        </div>
      </div>

      {/* Modal Exportar Pagamentos */}
      <Modal 
        show={showModalExportarPag} 
        onHide={() => setShowModalExportarPag(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Exportar Pagamentos</Modal.Title>
        </Modal.Header>
        <Form method="POST">
          <Modal.Body>
            <div className="container">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Ano Referência</label>
                  <input type="text" className="form-control form-control-sm" name="ano_referencia" />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Mês Referência</label>
                  <input type="text" className="form-control form-control-sm" name="mes_referencia" />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-info" onClick={() => setShowModalExportarPag(false)}>
              Fechar
            </button>
            <button type="submit" className="btn btn-primary">
              Exportar
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default Terapeutas